/**
 * AIThinkingBlock / ThinkingTranscript
 *
 * Reasoning surface that blends into the assistant chat background. It grows to
 * fit short thoughts and only becomes a fixed, scrolling window (with top/bottom
 * fades to the chat background) once the thought is taller than maxHeight.
 *
 * Primary responsibilities:
 * - Grow to the thought's natural height up to a cap; scroll past the cap
 * - Show fade overlays only when the content actually overflows
 * - Follow live text to the newest line while streaming
 *
 * Dependencies:
 * - lucide-react (spinner icon in the demo header)
 */

'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

// Run layout measurement before paint in the browser (no fade flash on first
// render), but fall back to useEffect on the server to avoid the SSR warning.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

export interface ThinkingTranscriptProps {
  // Reasoning text to display
  content: string
  // True while the model is still producing reasoning
  isStreaming?: boolean
  // Max height before the window starts scrolling (default 150px). Short
  // thoughts render shorter than this; only long ones hit the cap and scroll.
  maxHeight?: number
  className?: string
}

/**
 * Reasoning surface that blends into the assistant chat background. It grows to
 * fit short thoughts (no wasted empty box) and only becomes a fixed scrolling
 * window — with top/bottom fades to the chat background — once the thought is
 * taller than maxHeight. While streaming it pins to the newest line.
 */
export function ThinkingTranscript({
  content,
  isStreaming = false,
  maxHeight = 150,
  className
}: ThinkingTranscriptProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  // --------------

  // Measure overflow BEFORE paint so the fades are correct on first render
  // (no late flash). Fades + scroll only apply when content exceeds the cap,
  // so short thoughts stay clean and exact. ResizeObserver keeps it correct as
  // the thought grows during streaming.
  useIsomorphicLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return

    const measure = () => {
      // scrollHeight > clientHeight means content exceeds the capped box.
      setIsOverflowing(el.scrollHeight - el.clientHeight > 1)
    }

    measure()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [content, maxHeight])

  // --------------

  // While streaming, keep the window pinned to the latest reasoning line.
  useEffect(() => {
    if (!isStreaming || !contentRef.current) return
    const el = contentRef.current
    el.scrollTop = el.scrollHeight
  }, [content, isStreaming])

  // --------------

  const fadeHeight = Math.min(48, Math.round(maxHeight / 3))

  return (
    <div
      className={cn(
        // Blend into the assistant chat background — no card chrome. Height is
        // driven by content up to maxHeight, then it scrolls.
        'bg-background text-foreground/80 relative overflow-hidden rounded-lg',
        className
      )}
    >
      {/* Top fade — only when the thought overflows the capped height. */}
      {isOverflowing && (
        <div
          className="from-background pointer-events-none absolute left-0 right-0 top-0 z-10 bg-gradient-to-b from-30% to-transparent"
          style={{ height: fadeHeight }}
        />
      )}

      {/* Bottom fade — only when overflowing. */}
      {isOverflowing && (
        <div
          className="from-background pointer-events-none absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-30% to-transparent"
          style={{ height: fadeHeight }}
        />
      )}

      {/* Content: grows to fit, caps at maxHeight, then scrolls. */}
      <div
        ref={contentRef}
        className="overflow-y-auto px-1 py-1"
        style={{ maxHeight, scrollBehavior: 'auto' }}
      >
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{content}</p>
      </div>
    </div>
  )
}

interface AIThinkingBlockProps {
  // Reasoning text to display in the scrolling transcript
  content?: string
  // Label shown beside the spinner
  label?: string
  className?: string
}

const DEFAULT_CONTENT = `Okay, first I need to understand what HextaUI is. It seems to be a UI library or framework, likely for web development. I should check the web results for any mentions of HextaUI.

Looking at the web results, I see that HextaUI is mentioned in several contexts. It's described as a collection of modern, responsive, and customizable UI components for Next.js. It seems to be designed to help developers build websites more efficiently.

I should look for more specific details. The results mention that HextaUI provides components that can be copied, adapted, and personalized. It also mentions that it's open-source and has a community of contributors.

I should also check if there are any specific features or benefits mentioned. The results talk about the components being responsive and customizable, which are important for modern web development. It also mentions that the components are designed to be production-ready.

I think I have enough information to provide a comprehensive answer.`

export default function AIThinkingBlock({
  content = DEFAULT_CONTENT,
  label = 'HextaAI is thinking',
  className
}: AIThinkingBlockProps) {
  const [timer, setTimer] = useState(0)

  // --------------

  // Tick the elapsed-seconds timer
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimer(prev => prev + 1)
    }, 1000)

    return () => clearInterval(timerInterval)
  }, [])

  // --------------

  return (
    <div className={cn('flex max-w-xl flex-col p-3', className)}>
      <div className="mb-4 flex items-center justify-start gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <p
          className="animate-[shimmer_5s_linear_infinite] bg-[linear-gradient(110deg,#404040,35%,#fff,50%,#404040,75%,#404040)] bg-[length:200%_100%] bg-clip-text text-base text-transparent"
          style={{ animation: 'shimmer 5s linear infinite' }}
        >
          {label}
        </p>
        <span className="text-sm text-muted-foreground">{timer}s</span>
        <style jsx>{`
          @keyframes shimmer {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        `}</style>
      </div>

      <ThinkingTranscript content={content} isStreaming={false} />
    </div>
  )
}
